import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { isFunctionElement } from '../../element.model';
import { findRoots } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { createEnumeratedCollection, memoizeByKey } from '../../../context/computed-collection';
import type { InflectionParams } from './inflection.model';

/**
 * Creates inflection points for functions.
 * Inflection points are where the second derivative changes sign (concavity changes).
 * Uses numerical root-finding on the second derivative to find where f''(x) = 0.
 */
export class InflectionCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'inflection';
  override readonly computedCollectionKeys = [{ name: '', properties: ['x', 'y'] }] as const;

  private readonly maxInflectionPoints = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as InflectionParams | undefined;
    if (!params?.of) {
      throw new Error('Inflection feature requires params.of to specify the parent function');
    }
    const parentElement = context.elements.get(params.of);
    if (!parentElement || !isFunctionElement(parentElement)) {
      throw new Error(`Inflection feature requires a function element. Got: ${parentElement?.type ?? 'undefined'}`);
    }
    const parentFn = parentElement as FunctionElement;
    const objects: GeometryElement[] = [];

    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Memoized root-finding on second derivative
    const getInflectionPoints = memoizeByKey(
      () => {
        const bbox = board.getBoundingBox();
        const secondDerivative = parentFn.getDerivative(2);
        const xValues = findRoots(secondDerivative, bbox[0], bbox[2], {
          maxResults: this.maxInflectionPoints,
        });
        return xValues.map((x) => ({ x, y: parentFn.evaluate(x) }));
      },
      () => {
        const bbox = board.getBoundingBox();
        const fnParams = parentFn.getParams();
        return `${bbox[0].toFixed(2)},${bbox[2].toFixed(2)}|${Object.values(fnParams)
          .map((v) => v.toFixed(4))
          .join(',')}`;
      },
    );

    for (let i = 0; i < this.maxInflectionPoints; i++) {
      const idx = i;
      const result = elementFactory.point({
        coords: [
          () => {
            const points = getInflectionPoints();
            return idx < points.length ? points[idx].x : NaN;
          },
          () => {
            const points = getInflectionPoints();
            if (idx < points.length) {
              return isFinite(points[idx].y) ? points[idx].y : NaN;
            }
            return NaN;
          },
        ],
        name: 'inflection',
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    const collection = createEnumeratedCollection(getInflectionPoints, this.maxInflectionPoints);

    return {
      id,
      type: 'inflection',
      objects,
      getComputedCollections: () => ({ '': collection }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
